import styled from 'styled-components'
import { Colors, Spacing } from 'cio-design-library/design-tokens'
import Typography, { FontWeights } from 'builder/styles/typography'
import { ModalClose } from 'builder/components/ModalClose'
import Button from 'builder/components/Button'
import Media from 'builder/styles/media'

export const Container = styled.div`
  position: relative;
  display: flex;

  ${Media.Tablet`
    flex-direction: column;
    max-width: 640px;
    width: 100%;
  `};

  ${Media.Phone`
    flex-direction: column;
    width: 100%;
    max-width: calc(100% - 32px);
    margin: 16px;
  `};
`

export const CloseButton = styled(ModalClose)`
  position: absolute;
  top: 32px;
  right: 32px;
`

export const LeftSection = styled.div`
  padding: 32px 40px 0;
  display: flex;
  flex-direction: column;
  background-color: ${Colors.Neutral5};
  border-top-left-radius: ${Spacing.S};
  border-bottom-left-radius: ${Spacing.S};

  ${Media.Tablet`
    padding: 32px 40px 32px;
    border-top-right-radius: ${Spacing.S};
    border-bottom-left-radius: 0;
  `};

  ${Media.Phone`
    padding: 24px 24px 16px;
    border-radius: ${Spacing.S} ${Spacing.S} 0 0;
    border-bottom-left-radius: 0;
    flex-shrink: 0;
  `};
`

export const RightSection = styled.div<{ $isLoading: boolean; $isPayment: boolean }>`
  padding: ${props => (props.$isPayment ? '32px 40px 40px' : '90px 40px 40px')};
  ${props => props.$isPayment && 'min-width: 496px;'}
  background-color: ${Colors.White};
  display: flex;
  flex-direction: column;
  border-top-right-radius: ${Spacing.S};
  border-bottom-right-radius: ${Spacing.S};

  ${props =>
    props.$isLoading &&
    `
    height: 633px;
    align-items: center;
    justify-content: center;
    padding: 0 80px;
  `}

  ${Media.Tablet`
    padding: 32px 40px 40px;
    min-width: unset;
    border-top-left-radius: 0;
    border-bottom-left-radius: ${Spacing.S};
    border-top-right-radius: 0;
  `};

  ${Media.Phone`
    padding: 24px 24px 32px;
    min-width: unset;
    border-radius: 0 0 ${Spacing.S} ${Spacing.S};
  `};
`

export const Crown = styled.img`
  width: 50px;
  height: 32px;
  margin-bottom: 13px;
`

export const Title = styled.h1`
  ${Typography.M};
  ${FontWeights.DemiBold};
  color: ${Colors.Neutral90};
`

export const Subtitle = styled.p`
  ${Typography.Body};
  color: ${Colors.Neutral50};
  margin: 6px 0 ${Spacing.L};
`

export const FeatureList = styled.ul`
  gap: 12px;
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
`

export const FeatureItem = styled.li<{ $hideWrap: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;

  ${props =>
    !props.$hideWrap &&
    Media.Phone`
    flex-wrap: wrap;

    .bold + .email {
      flex-basis: 100%;
      margin-left: 30px;
      margin-top: -9px;
    }
  `}

  ${props =>
    props.$hideWrap &&
    Media.Phone`
    .bold + .email {
      flex-basis: 100%;
      margin-left: 30px;
      margin-top: -9px;
    }
  `};
`

export const FeatureIcon = styled.img`
  width: 24px;
  height: 24px;
  color: ${Colors.Neutral90};
`

export const FeatureText = styled.span`
  ${Typography.Caption};
  color: ${Colors.Neutral90};

  &.bold {
    ${FontWeights.Medium};

    ${Media.Phone`
      font-weight: ${FontWeights.Regular};
    `};
  }

  &.email {
    ${FontWeights.Medium};
    color: ${Colors.Blue50};
  }
`

export const PricingOption = styled.div<{ $selected?: boolean; $marginTop: boolean }>`
  border: 2px solid ${props => (props.$selected ? Colors.Blue50 : Colors.Neutral15)};
  border-radius: 16px;
  padding: ${Spacing.S};
  margin-bottom: 12px;
  cursor: pointer;
  position: relative;
  transition: border-color 0.2s ease;
  width: 416px;
  ${props => props.$marginTop && 'margin-top: 12px;'}

  &:hover {
    border-color: ${Colors.Blue50};
  }

  ${Media.Tablet`
    width: 100%;
    max-width: 560px;
  `};

  ${Media.Phone`
    width: 100%;
    padding: 20px;
    margin-bottom: ${Spacing['2XS']};
    border-radius: 12px;
    min-height: 80px;
  `};
`

export const RadioButton = styled.div<{ $selected?: boolean }>`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => (props.$selected ? Colors.Blue50 : Colors.Neutral30)};
  border-radius: 50%;
  margin-right: ${Spacing.S};
  position: relative;
  flex-shrink: 0;

  ${props =>
    props.$selected &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 8px;
      height: 8px;
      background-color: ${Colors.Blue50};
      border-radius: 50%;
    }
  `}
`

export const PricingOptionContent = styled.div`
  display: flex;
`

export const PricingDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing['2XS']};
`

export const PricingPeriod = styled.div`
  ${Typography.Body};
  ${FontWeights.Medium};
  color: ${Colors.Neutral90};
`

export const PricingAmount = styled.div`
  ${Typography.S};
  ${FontWeights.Medium};
  color: ${Colors.Neutral90};
`

export const SaveBadge = styled.div`
  position: absolute;
  right: 16px;
  padding: 4px 8px 4px 12px;
  top: -12px;
  text-transform: uppercase;
  color: white;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.3px;
  border-radius: 4px;
  background: linear-gradient(94deg, #9ba1fb 0%, #4c89f3 47.53%, #54aff8 100%), #eaf6ff;
`

export const NextButton = styled(Button)`
  margin-top: 95px;
  width: 100%;

  ${Media.Phone`
    margin-top: 32px;
    padding: 16px;
    font-size: 1.1rem;
  `};
`
